import {
  FilterValues,
  ListFilterState,
  Names,
  ReadFilterState,
  Subjects,
  UseFilterSetValue,
  UseListFilterGetValues,
  UseListFilterProps,
  UseListFilterReturn,
  UseListFilterWatch,
} from "./types";
import { cloneDeep, isBoolean, set } from "lodash";
import { isString, isUndefined } from "./utils";
import generateWatchOutput from "./generate-watch-output";
import isWatched from "./is-watched";
import createSubject from "./create-subject";
import { FieldPath } from "react-hook-form/dist/types";
import { DeepPartial } from "@towersystems/roam-common/lib/shared-types";
import { get } from "react-hook-form";

export function createFilterControl<
  TFilterValues extends FilterValues = FilterValues
>(
  props: UseListFilterProps<TFilterValues> = {}
): Omit<UseListFilterReturn<TFilterValues>, "filterState"> {
  let _options = {
    ...props,
  };

  let _state = {
    watch: false,
    mount: false,
  };

  let _defaultValues = _options.defaultValues || {};
  let _filterValues = cloneDeep(_defaultValues);

  let _filterState: ListFilterState<TFilterValues> = {
    activeFiltersCount: 0,
  };

  const _proxyFilterState: ReadFilterState = {
    activeFiltersCount: false,
  };

  let _names: Names = {
    watch: new Set(),
  };

  const _subjects: Subjects<TFilterValues> = {
    values: createSubject(),
    array: createSubject(),
    state: createSubject(),
  };

  const setFilterValue: UseFilterSetValue<TFilterValues> = (name, value) => {
    const cloneValue = cloneDeep(value);
    set(_filterValues, name, cloneValue);

    isWatched(name as any, _names) && _subjects.state.next({ ..._filterState });

    _subjects.values.next({
      name: _state.mount ? (name as any) : undefined,
      values: { ..._filterValues },
    });

    _updateActiveFiltersCount();
  };

  const clearAll = () => {
    _filterValues = {};
    _defaultValues = {};
    _subjects.state.next({ ..._filterState });
    _subjects.values.next({
      name: undefined,
      values: { ..._filterValues },
    });
    _updateActiveFiltersCount();
  };

  const getFilterValues: UseListFilterGetValues<TFilterValues> = (
    fieldNames?: FieldPath<TFilterValues>
  ) => {
    const values = {
      ..._defaultValues,
      ..._filterValues,
    };
    return isUndefined(fieldNames) ? values : get(values, fieldNames);
  };

  const watch: UseListFilterWatch<TFilterValues> = (
    name?: FieldPath<TFilterValues>,
    defaultValue?: DeepPartial<TFilterValues>
  ) => {
    return generateWatchOutput(name, _names, _filterValues, true, defaultValue);
  };

  const _updateActiveFiltersCount = () => {
    let total = 0;

    for (const [_, value] of Object.entries(_filterValues)) {
      if (isString(value) && value.length > 0) {
        total += 1;
      } else if (value instanceof Array && value.length > 0) {
        total += value.length;
      } else if (isBoolean(value) && value === true) {
        total += 1;
      }
    }
    _subjects.state.next({
      activeFiltersCount: total,
    });
  };

  const _updateFilterState = (
    updatedFilterState: Partial<ListFilterState<TFilterValues>>
  ) => {
    _filterState = {
      ..._filterState,
      ...updatedFilterState,
    };
  };

  return {
    control: {
      _subjects,
      _proxyFilterState,
      _updateFilterState,
      _updateActiveFiltersCount,
      get _state() {
        return _state;
      },
      get _filterValues() {
        return _filterValues;
      },
      get _filterState() {
        return _filterState;
      },
      get _defaultValues() {
        return _defaultValues;
      },
    },
    clearAll,
    setFilterValue,
    getFilterValues,
    watch,
  };
}
